import React, { useState, useEffect, useCallback } from 'react';
// import MUI components
import { Grid2 as Grid, Alert, Snackbar } from '@mui/material';
// import custom components
import { BoxCenterStart } from '../style/box';
import { TypographyBold } from '../style/typography';
import { commonStyles } from "../style/common";
import CommonCard from '../components/common_card';
import { BaseDialog } from '../components/base_dialog';
import RecipientRegistration from "../components/recipient_registration";
import RecipientList from '../components/recipient_list';
import { validationSchemaRegistRecipient, validationSchemaEditRecipient } from '../func/validation_check';
import { fetchData, postData } from "../func/api";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DragDropContext } from '@hello-pangea/dnd';
import SkeletonLoading from "../components/skeleton_loading";
import { REPORT_STATUS } from '../const/config';


const MstRecipientList = () => {
    const [sendCategories, setSendCategories] = useState([]);
    const [expandedRecipient, setExpandedRecipient] = useState(false);
    const [editRecipient, setEditRecipient] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [recipientToDelete, setRecipientToDelete] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState(null);
    const [dialogData, setDialogData] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [internalRecipients, setInternalRecipients] = useState([]);
    const [governmentRecipients, setGovernmentRecipients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const { control: registControl, handleSubmit: handleRegistSubmit, reset: registReset, getValues } = useForm({
        resolver: yupResolver(validationSchemaRegistRecipient()),
        defaultValues: {
            registRecipient: {
                name: "",
                department_name: "",
                email: "",
                send_category: ""
            }
        },
        mode: "onBlur"
    });

    const { control: editControl, handleSubmit: handleEditSubmit, reset: editReset } = useForm({
        resolver: yupResolver(validationSchemaEditRecipient),
        defaultValues: {
            editRecipient: {
                name: "",
                department_name: "",
                email: "",
                send_category: ""
            }
        },
        mode: "onBlur"
    });

    // データ取得
    useEffect(() => {
        // 送付先カテゴリ
        setSendCategories([
            { value: "1", label: "国土交通省" },
            { value: "2", label: "内部" }
        ]);

        const fetchDataAll = async () => {
            try {
                const [governmentRecipientList, internalRecipientList] = await Promise.all([
                    fetchData(`api/get_send_category_recipient_list/1`),
                    fetchData(`api/get_send_category_recipient_list/2`)
                ]);

                setGovernmentRecipients(governmentRecipientList); // 国土交通省送付先一覧
                setInternalRecipients(internalRecipientList); // 内部送付先一覧

                // 地震一覧データ取得
                const earthquakeData = await fetchData(`api/get_earthquake_list/`);
                // 業務対中の場合は、編集不可
                const reportingData = earthquakeData.find(obj =>
                    obj.report_status_uuid === REPORT_STATUS.UNSUPPORTED ||
                    obj.report_status_uuid === REPORT_STATUS.FISRTREPORT ||
                    obj.report_status_uuid === REPORT_STATUS.PRIMARYCHECK ||
                    obj.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTSUPPORT
                );
                setIsDisabled(reportingData ? true : false);
            } catch (error) {
                console.error("データ取得中にエラーが発生しました:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDataAll();
    }, [refreshFlag]);

    // 編集対象の送付先が変わったらフォームをリセット
    useEffect(() => {
        if (editRecipient) {
            editReset({
                editRecipient: {
                    uuid: editRecipient.uuid,
                    name: editRecipient.name,
                    department_name: editRecipient.department_name,
                    email: editRecipient.email,
                    send_category: editRecipient.send_category
                }
            });
        }
    }, [editRecipient, editReset]);

    const openDialog = (type, data = null) => {
        setDialogType(type);
        setDialogData(data);
        setDialogOpen(true);
    };

    const handleCancelEdit = () => {
        setExpandedRecipient(false);
        setEditRecipient(null);
    };

    const handleDialogConfirm = () => {
        switch (dialogType) {
            case 'register':
                handleRegistSubmit(handleConfirm)(); // 登録処理
                break;
            case 'delete':
                handleDeleteConfirm(); // 削除処理
                break;
            case 'save':
                handleSaveConfirm(); // 保存処理
                break;
            default:
                break;
        }
        setDialogOpen(false);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
    };

    // 「送付先を登録」ボタンのダイアログ「OK」押下時
    const handleConfirm = async () => {
        try {
            const values = getValues("registRecipient");
            const sendCat = Number(values.send_category);

            let dataList = [];

            // 登録対象の report_category を決定
            if (sendCat === 2) {
                // 内部 → report_category 1 と 2
                dataList = [1, 2].map(rc => ({ ...values, send_category: sendCat, report_category: rc }));
            } else if (sendCat === 1) {
                // 国土交通省 → report_category 1 のみ
                dataList = [{ ...values, send_category: sendCat, report_category: 1 }];
            }

            // カテゴリごとにsort_num最大値を取得
            let maxSortNum;
            if (sendCat === 2) {
                // 内部は internalRecipients の sort_num 最大値
                maxSortNum = internalRecipients.length > 0
                    ? Math.max(...internalRecipients.map(r => r.sort_num || 0))
                    : 0;
            } else if (sendCat === 1) {
                // 国土交通省は governmentRecipients の sort_num 最大値
                maxSortNum = governmentRecipients.length > 0
                    ? Math.max(...governmentRecipients.map(r => r.sort_num || 0))
                    : 0;
            }

            let nextSortNum = maxSortNum + 1;

            // 複数レコードを順次登録
            for (const data of dataList) {
                const postDataObj = {
                    name: data.name,
                    department_name: data.department_name,
                    email: data.email,
                    report_category: data.report_category,
                    send_category: data.send_category,
                    sort_num: nextSortNum
                };

                await postData("api/post_recipient", postDataObj);

                nextSortNum++; // 次のレコードは +1
            }

            setAlertMessage("登録が完了しました");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
            registReset();
        } catch (err) {
            setAlertMessage("登録に失敗しました");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };

    const handleEditClick = (recipient) => {
        setEditRecipient({ ...recipient });
        setExpandedRecipient(recipient.uuid);
    };

    const handleDeleteClick = (recipient) => {
        // 削除対象リストを判定
        const targetList = recipient.send_category === 2
            ? internalRecipients
            : governmentRecipients;

        if (targetList.length === 1) {
            setAlertMessage("送付先は最低1つ必要です。");
            setAlertSeverity("error");
            setAlertOpen(true);
            return;
        }
        openDeleteDialog(recipient);
        setRecipientToDelete(recipient);
        setDeleteDialogOpen(true);
    };

    const openDeleteDialog = (recipient) => {
        setDialogType("delete");
        setDialogData(recipient); // 削除対象
        setDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            // どのリストを更新するか決定
            const targetList = recipientToDelete.send_category === 2
                ? [...internalRecipients]
                : [...governmentRecipients];

            // 削除＋sort_num詰め
            const updatedList = targetList
                .filter(r => r.uuid !== recipientToDelete.uuid)
                .sort((a, b) => a.sort_num - b.sort_num)
                .map((r, index) => ({ ...r, sort_num: index + 1 }));

            // DB 更新（削除したものは論理削除済みとして扱う）
            for (const recipient of updatedList) {
                await postData("api/post_update_recipient_sort_num", {
                    uuid: recipient.uuid,
                    sort_num: recipient.sort_num
                });
            }

            // state に反映
            if (recipientToDelete.send_category === 2) {
                setInternalRecipients(updatedList);
            } else {
                setGovernmentRecipients(updatedList);
            }

            // 論理削除 API
            await postData("api/post_delete_recipient", { uuid: recipientToDelete.uuid });

            setAlertMessage("送付先の削除と順番の更新が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (err) {
            console.error("削除失敗しました：", err);
            setAlertMessage("送付先の削除に失敗しました。再度お試しください。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setDeleteDialogOpen(false);
            setRecipientToDelete(null);
            setExpandedRecipient(false);
            setEditRecipient(null);
            setRefreshFlag(prev => !prev);
        }
    };

    const handleSaveConfirm = async () => {
        try {
            const updatedData = {
                ...dialogData,
                sort_num: dialogData.sort_num,
                send_category: Number(dialogData.send_category),
                report_category: Number(dialogData.report_category)
            };

            // DB 更新
            await postData("api/post_update_recipient", updatedData);

            // state 更新（カテゴリ変更なし前提）
            if (updatedData.send_category === 2) {
                setInternalRecipients(prev =>
                    prev.map(r => r.uuid === updatedData.uuid ? updatedData : r)
                );
            } else {
                setGovernmentRecipients(prev =>
                    prev.map(r => r.uuid === updatedData.uuid ? updatedData : r)
                );
            }

            setAlertMessage("送付先の更新が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
        } catch (err) {
            console.error("更新失敗しました：", err);
            setAlertMessage("送付先の更新に失敗しました。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setExpandedRecipient(false);
            setEditRecipient(null);
            setDialogOpen(false);
        }
    };

    const onDragEnd = useCallback(async (result) => {
        const { source, destination } = result;
        if (!destination || source.droppableId !== destination.droppableId) return;

        const reorder = (list, startIndex, endIndex) => {
            const result = Array.from(list);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        };

        let updatedList = [];
        if (source.droppableId === 'internal-recipients') {
            updatedList = reorder(internalRecipients, source.index, destination.index)
                .map((r, index) => ({ ...r, sort_num: index + 1 }));
            setInternalRecipients(updatedList);
        } else if (source.droppableId === 'government-recipients') {
            updatedList = reorder(governmentRecipients, source.index, destination.index)
                .map((r, index) => ({ ...r, sort_num: index + 1 }));
            setGovernmentRecipients(updatedList);
        }

        // DB 更新
        try {
            for (const recipient of updatedList) {
                await postData("api/post_update_recipient_sort_num", {
                    uuid: recipient.uuid,
                    sort_num: recipient.sort_num
                });
            }
            setAlertMessage("送付先の並び順を更新しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
        } catch (err) {
            console.error("並び順更新に失敗しました：", err);
            setAlertMessage("並び順の更新に失敗しました。再度お試しください。");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    }, [internalRecipients, governmentRecipients]);

    if (loading) {
        return <SkeletonLoading />;
    }

    return (
        <BoxCenterStart sx={commonStyles.page}>
            <Grid
                container
                spacing={7}
                columns={12}
                sx={commonStyles.container}
            >
                {/* Page Title */}
                <Grid size={{ xs: 12 }}>
                    <CommonCard
                        size={{ xs: 12 }}
                        child={
                            <TypographyBold
                                sx={{ textAlign: 'center', padding: '10px 0' }}
                            >
                                送付先管理
                            </TypographyBold>
                        }
                    ></CommonCard>
                </Grid>
                {/* 新規送付先登録 */}
                <RecipientRegistration
                    control={registControl}
                    sendCategories={sendCategories}
                    openDialog={openDialog}
                    handleRegistSubmit={handleRegistSubmit}
                    expanded={true}
                    isDisabled={isDisabled}
                />
                <DragDropContext onDragEnd={onDragEnd}>
                    {/* 内部送付先一覧 */}
                    <RecipientList
                        recipients={internalRecipients}
                        sendCategories={sendCategories}
                        control={editControl}
                        expandedRecipient={expandedRecipient}
                        handleEditClick={handleEditClick}
                        handleCancelEdit={handleCancelEdit}
                        handleDeleteClick={handleDeleteClick}
                        handleEditSubmit={handleEditSubmit}
                        openDialog={openDialog}
                        title="内部送付先一覧"
                        droppableId="internal-recipients"
                        isDisabled={isDisabled}
                    />
                    {/* 国土交通省送付先一覧 */}
                    <RecipientList
                        recipients={governmentRecipients}
                        sendCategories={sendCategories}
                        control={editControl}
                        expandedRecipient={expandedRecipient}
                        handleEditClick={handleEditClick}
                        handleCancelEdit={handleCancelEdit}
                        handleDeleteClick={handleDeleteClick}
                        handleEditSubmit={handleEditSubmit}
                        openDialog={openDialog}
                        title="国土交通省送付先一覧"
                        droppableId="government-recipients"
                        isDisabled={isDisabled}
                    />
                </DragDropContext>
                {/* 確認ダイアログ */}
                <BaseDialog
                    open={dialogOpen}
                    onClose={handleDialogCancel}
                    title={
                        dialogType === "register" ? "送付先登録" :
                            dialogType === "delete" ? "送付先削除" :
                                dialogType === "save" ? "送付先保存" : ""
                    }
                    message={
                        dialogType === "register" ? "送付先を登録します。よろしいですか？" :
                            dialogType === "delete" ? `送付先「${dialogData?.name}」を削除しますか？\nこの操作は取り消せません。` :
                                dialogType === "save" ? "編集内容を保存します。よろしいですか？" : ""
                    }
                    onConfirm={handleDialogConfirm}
                    confirmText={
                        dialogType === "register" ? "OK" :
                            dialogType === "delete" ? "削除" :
                                dialogType === "save" ? "保存" : "OK"
                    }
                    cancelText="キャンセル"
                />
                {/* 失敗時のSnackbar */}
                {alertMessage && (
                    <Snackbar
                        open={alertOpen}
                        autoHideDuration={3000}
                        onClose={() => setAlertOpen(false)}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                        <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                            {alertMessage}
                        </Alert>
                    </Snackbar>
                )}
            </Grid>
        </BoxCenterStart>
    );
};

export default MstRecipientList;